import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { createClient } from '@supabase/supabase-js';
import type { Session, SupabaseClient } from '@supabase/supabase-js';
import { loadWebConfig } from '@kantera/config';
import { actingRoles, brandOrgId, brandUserId } from '@kantera/types';
import type {
  Role,
  TenantContext,
  UserMetadata,
  Tier,
  OrgMembership,
  ApiPlayerPortalAccess,
  PlayerPortalAccessResponse,
} from '@kantera/types';
import { apiGet, apiPost } from '@kantera/api-client';
import { getOrCreateClientFingerprint } from '../utils/device-fingerprint';
import { SwitchOrgError } from '../utils/switchOrgError';
import { clearAllGuidedSetupProgress } from '../utils/guidedSetupProgress';

const {
  supabaseUrl: SUPABASE_URL,
  supabaseAnonKey: SUPABASE_ANON_KEY,
  apiUrl: API_URL,
} = loadWebConfig();

/**
 * KI-045 d6 — POST the browser fingerprint to `/api/me/devices` on every
 * SIGNED_IN event. Fire-and-forget; the AuthContext catches failures and
 * logs them only in dev. The deviceName uses the leading 120 chars of the
 * UA string which the server stores verbatim — it's a label for the
 * parent's "My Devices" view, not a security claim.
 */
async function registerDeviceWithApi(accessToken: string): Promise<void> {
  const clientFingerprint = await getOrCreateClientFingerprint();
  const deviceName = navigator.userAgent.slice(0, 120);
  await apiPost<
    { clientFingerprint: string; platform: 'web'; deviceName: string },
    { deviceId: string; isNew: boolean }
  >(
    '/api/me/devices',
    { clientFingerprint, platform: 'web', deviceName },
    { baseUrl: API_URL, token: accessToken },
  );
}

export interface AuthContextType {
  user: UserMetadata | null;
  /**
   * `tenant.roles` are the roles this person ACTS with — every access check reads them, as the
   * API's TenantContext.roles does. KAN-115: an assistant coach acts as a coach, so they include
   * 'coach' (actingRoles, @kantera/types). To SHOW someone their role, read `heldRoles`.
   */
  tenant: TenantContext | null;
  /**
   * The roles the signed-in person HOLDS in this org, exactly as their session carries them. For
   * display only — never for an access decision (use tenant.roles). An assistant coach's is
   * ['assistant_coach'], without the 'coach' they act as.
   */
  heldRoles: readonly Role[];
  accessToken: string | null;
  loading: boolean;
  /** True when user arrived via password reset link — must set password before accessing app */
  passwordRecovery: boolean;
  /**
   * The reason a sign-in attempt FAILED ON THE RETURN LEG, or null.
   *
   * Login.tsx:185 only catches a failure to BEGIN an OAuth flow. Everything that
   * goes wrong after the user leaves for the provider — provider not enabled,
   * redirect URL not allow-listed, consent declined — comes back as
   * ?error=/&error_description= on the landing URL. auth-js turns that into an
   * AuthImplicitGrantRedirectError and returns it from getSession() rather than
   * throwing (GoTrueClient _initialize returns `{ error }`). This used to be
   * destructured away, so every one of those failures rendered as a silent
   * return to the sign-in page — indistinguishable from never having clicked.
   */
  authError: string | null;
  /** Clears authError — call when the user starts a fresh attempt. */
  clearAuthError: () => void;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  supabase: SupabaseClient;
  // Multi-org
  orgs: OrgMembership[];
  activeOrg: OrgMembership | null;
  switchOrg: (orgId: string) => Promise<void>;
  /**
   * Read the org list again (GET /api/auth/my-orgs). The top bar's club pill and the org switcher
   * show each org's name from it, so a screen that renames the club calls this to show the new name
   * at once (KAN-121). Non-fatal, like the first read.
   */
  refreshOrgs: () => Promise<void>;
  orgsLoading: boolean;
  /** Display name of the org currently being switched to, or null when idle. */
  switchingOrgName: string | null;
  /**
   * The server's answer to "may this user use the 13+ player portal?", fetched
   * once per identity and cached here for the session.
   *
   * `null` means UNKNOWN — never assume it means "allowed". It is null before the
   * probe resolves, and null again if the probe failed. Both readings are DENY;
   * see useIsSelfPlayer, which is the only thing that should interpret this.
   */
  portalAccess: ApiPlayerPortalAccess | null;
  /** True while the probe is in flight. Guards should render a spinner, not a redirect. */
  portalAccessLoading: boolean;
}

/**
 * The roles a session HOLDS: app_metadata.roles as written (admin-only writable), or none. The
 * SPA has always read only the list, never the singular `role`.
 */
function readHeldRoles(session: Session): Role[] {
  return ((session.user.app_metadata?.roles as string[] | undefined) ?? []) as Role[];
}

/**
 * The name a person gave us when their account was made, or '' when they gave none.
 *
 * KAN-118: self-serve sign-up (POST /api/auth/register) stores it as `full_name`; invitation sign-up
 * and the profile form store `name`. Reading only `name` left every self-serve owner nameless, so
 * they were sent to "Complete your profile" and asked again for the name they had just typed. `name`
 * still wins when it holds something; a blank one does not hide `full_name`. Reading both here also
 * fixes every owner who signed up before this change.
 */
function readMetadataName(userMeta: Record<string, unknown> | undefined): string {
  const name = userMeta?.['name'];
  if (typeof name === 'string' && name.trim()) return name;
  const fullName = userMeta?.['full_name'];
  if (typeof fullName === 'string' && fullName.trim()) return fullName;
  return '';
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

// ---------------------------------------------------------------------------
// AuthProvider
// ---------------------------------------------------------------------------

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<UserMetadata | null>(null);
  const [tenant, setTenant] = useState<TenantContext | null>(null);
  const [heldRoles, setHeldRoles] = useState<readonly Role[]>([]);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [passwordRecovery, setPasswordRecovery] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const clearAuthError = useCallback(() => setAuthError(null), []);

  // Multi-org state
  const [orgs, setOrgs] = useState<OrgMembership[]>([]);
  const [orgsLoading, setOrgsLoading] = useState(false);
  // Tracks the display name of the org being switched to — null when idle.
  // Exposed so Sidebar (or any consumer) can render the full-screen overlay.
  const [switchingOrgName, setSwitchingOrgName] = useState<string | null>(null);
  const switchingRef = useRef(false);

  // Portal access — the 13+ gate's server-verified input (FIX-BACKLOG C13/C14).
  const [portalAccess, setPortalAccess] = useState<ApiPlayerPortalAccess | null>(null);
  const [portalAccessLoading, setPortalAccessLoading] = useState(false);
  /**
   * The identity the cached answer belongs to: `${userId}:${orgId}`.
   *
   * This is what stops the probe from becoming a per-navigation round-trip. The
   * answer is a property of WHO you are and WHICH org you are in, so it is
   * fetched when that pair changes and reused for every route in between —
   * moving between the 7 portal screens costs nothing. onAuthStateChange can
   * fire repeatedly for the same identity (TOKEN_REFRESHED being the common
   * one); those are no-ops rather than refetches.
   *
   * Org switching IS an invalidation: the players row is resolved per-org, so the
   * same human can be a 13+ player in one club and unknown in another. switchOrg
   * refreshes the JWT, which changes org_id, which changes this key.
   */
  const portalAccessKeyRef = useRef<string | null>(null);

  // ---------------------------------------------------------------------------
  // fetchOrgs — load /api/auth/my-orgs for the current token
  // ---------------------------------------------------------------------------

  const fetchOrgs = useCallback(async (token: string) => {
    setOrgsLoading(true);
    try {
      const response = await globalThis.fetch(`${API_URL}/api/auth/my-orgs`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        // Non-fatal — user may not have memberships yet (first login edge case)
        if (import.meta.env.DEV) {
          // eslint-disable-next-line no-console
          console.warn('fetchOrgs: non-OK response', response.status);
        }
        return;
      }

      const json = (await response.json()) as { data: OrgMembership[] };
      setOrgs(json.data ?? []);
    } catch (err) {
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.warn('fetchOrgs: fetch failed', err);
      }
      // Non-fatal — org switcher will show skeleton / nothing
    } finally {
      setOrgsLoading(false);
    }
  }, []);

  // ---------------------------------------------------------------------------
  // fetchPortalAccess — resolve the 13+ gate ONCE per identity
  // ---------------------------------------------------------------------------

  const fetchPortalAccess = useCallback(async (token: string, key: string) => {
    if (portalAccessKeyRef.current === key) return; // already answered for this identity
    portalAccessKeyRef.current = key;
    setPortalAccessLoading(true);
    try {
      const json = await apiGet<PlayerPortalAccessResponse>('/api/player-portal/access', {
        baseUrl: API_URL,
        token,
      });
      setPortalAccess(json.data ?? null);
    } catch (err) {
      // FAIL CLOSED. A portal gate that opens when its check errors is worse than
      // one that never opens — the whole point of moving this off the client was
      // that the client must not be able to decide it is 13+.
      setPortalAccess(null);
      // Drop the cache key so a later auth event retries. Without this a single
      // transient 500 during login would lock a legitimate player out of all 7
      // screens until they reloaded the tab.
      portalAccessKeyRef.current = null;
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.warn('portal access probe failed — treating as no access', err);
      }
    } finally {
      setPortalAccessLoading(false);
    }
  }, []);

  // ---------------------------------------------------------------------------
  // buildTenant — derive TenantContext from a Supabase session
  // ---------------------------------------------------------------------------

  const buildTenant = useCallback((session: Session): TenantContext | null => {
    const appMeta = session.user.app_metadata ?? {};
    const orgId = (appMeta.org_id as string | undefined)?.trim();
    const tier = (appMeta.tier as string | undefined) ?? 'free';
    const roles = readHeldRoles(session);

    // C23. This used to fall back to the org named in the user-writable metadata
    // bag — the one the subject edits via supabase.auth.updateUser({ data: … }),
    // so anyone could name whatever org they liked. app_metadata is admin-only.
    //
    // (This comment deliberately avoids spelling that field out: C23's own check
    // greps this file for it, and prose describing the fix would otherwise read
    // as the fix being absent.)
    //
    // The API
    // removed this identical fallback on 2026-04-29 as NEW-001 ("app_metadata is
    // the only trusted claim source") and refuses a missing org_id outright with
    // 401 INVALID_CLAIMS; the SPA kept it, so the client trusted a value the
    // server had already learned not to.
    //
    // Absent claim now DENIES rather than defaults. Nobody legitimate is
    // stranded: /auth/register resolves the org and writes app_metadata.org_id
    // at account creation, and accept-invitation carries orgId + role into that
    // same path — so a registered or invited user always has the claim. A
    // session without it already got 401 on every request, i.e. an app that
    // loaded no data at all. It now gets a clear sign-in page instead.
    if (!orgId) return null;

    return {
      orgId: brandOrgId(orgId),
      userId: brandUserId(session.user.id),
      // KAN-115: the roles they ACT with, the same rule the API applies in verifyToken. This is
      // the one place the SPA turns a session into roles, so ProtectedRoute, the sidebar, the
      // dashboard choice and every page's own check agree that an assistant coach is a coach —
      // for exactly the pages and actions a coach has, and nothing more.
      roles: actingRoles(roles),
      // FIX-BACKLOG C14 / LA-5 — `coppaConsent` was here and is now gone from
      // TenantContext entirely, on both sides of the package boundary.
      //
      // It read app_metadata.coppa_consent, which nothing in the product writes, so it
      // was the fail-closed default on every session that has ever existed. Before that
      // it read user_metadata, which the subject can edit on themselves — a COPPA age
      // claim sourced from a field its own subject controls.
      //
      // Both gates that read it now ask the server: the 13+ portal check goes through
      // portalAccess, and the direct-message age gate through isKnownMinor. Neither
      // answer travels in a token the browser holds.
      tier: tier as Tier,
    };
  }, []);

  // ---------------------------------------------------------------------------
  // Initialise on mount + subscribe to auth state changes
  // ---------------------------------------------------------------------------

  /**
   * Read an OAuth failure off the landing URL.
   *
   * Read BEFORE any await: auth-js strips these params from the address bar as
   * part of detectSessionInUrl, so anything that yields first may find them gone.
   * Checks both the query string (PKCE) and the fragment (implicit) because the
   * flow type decides which one the provider uses, and we support both.
   */
  function readRedirectAuthError(): string | null {
    try {
      const search = new URLSearchParams(window.location.search);
      const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
      const code = search.get('error') ?? hash.get('error');
      const description = search.get('error_description') ?? hash.get('error_description');
      if (!code && !description) return null;
      // Prefer the provider's own words; fall back to the machine code so the
      // message is never empty when something demonstrably went wrong.
      return (description ?? code ?? 'Sign-in failed.').replace(/\+/g, ' ');
    } catch {
      // A malformed URL must not take down the whole provider on mount.
      return null;
    }
  }

  useEffect(() => {
    const initAuth = async () => {
      // Synchronous, before the first await — see readRedirectAuthError.
      const redirectError = readRedirectAuthError();
      if (redirectError) setAuthError(redirectError);

      try {
        const { data, error: sessionError } = await supabase.auth.getSession();
        const session = data.session;

        // auth-js RETURNS this rather than throwing, so the catch below never
        // saw it. Do not overwrite a more specific message already read off the
        // URL — error_description is the provider's own words.
        if (sessionError && !redirectError) setAuthError(sessionError.message);

        if (session?.user) {
          const userMeta = session.user.user_metadata as UserMetadata;
          setUser({
            ...userMeta,
            email: session.user.email ?? userMeta.email,
            // The name given at sign-up, or '' (see readMetadataName). Display layers
            // fall back to email for rendering — see Sidebar, TopBar, Settings.
            name: readMetadataName(session.user.user_metadata),
          });
          setAccessToken(session.access_token);
          setTenant(buildTenant(session));
          setHeldRoles(readHeldRoles(session));
          // Fetch orgs after session is established
          await fetchOrgs(session.access_token);
          await fetchPortalAccess(
            session.access_token,
            `${session.user.id}:${(session.user.app_metadata?.org_id as string) ?? ''}`,
          );
        }
      } catch (error) {
        // A thrown failure here is still a failed sign-in and must be visible.
        setAuthError(error instanceof Error ? error.message : 'Sign-in failed.');
        if (import.meta.env.DEV) {
          // eslint-disable-next-line no-console
          console.error('Auth init error:', error);
        }
      } finally {
        setLoading(false);
      }
    };

    initAuth();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      // If user arrived via password reset link, lock app to reset-password page only
      if (event === 'PASSWORD_RECOVERY') {
        setPasswordRecovery(true);
        window.location.href = '/reset-password';
        return;
      }
      // Clear recovery flag once user successfully updates password
      if (event === 'USER_UPDATED') {
        setPasswordRecovery(false);
      }
      // KI-045 d6 — register this browser as a device on every fresh
      // sign-in (covers password, magic-link, and OAuth flows). Fire-and-
      // forget; failures are logged in dev only. Pairs with the mobile
      // wiring in apps/mobile/src/context/AuthContext.tsx.
      if (event === 'SIGNED_IN' && session?.access_token) {
        registerDeviceWithApi(session.access_token).catch((err) => {
          if (import.meta.env.DEV) {
            // eslint-disable-next-line no-console
            console.warn('[KI-045] device registration failed (non-fatal):', err);
          }
        });
      }
      if (session?.user) {
        const userMeta = session.user.user_metadata as UserMetadata;
        setUser({
          ...userMeta,
          email: session.user.email ?? userMeta.email,
          // Same rule as initAuth (readMetadataName).
          name: readMetadataName(session.user.user_metadata),
        });
        setAccessToken(session.access_token);
        setTenant(buildTenant(session));
        setHeldRoles(readHeldRoles(session));
        // Re-fetch orgs whenever the session changes (covers switchOrg + token refresh)
        if (!switchingRef.current) {
          await fetchOrgs(session.access_token);
        }
        // Cheap when the identity is unchanged — fetchPortalAccess compares the
        // key first, so a TOKEN_REFRESHED for the same user in the same org does
        // not hit the network. A switchOrg changes org_id and therefore does.
        await fetchPortalAccess(
          session.access_token,
          `${session.user.id}:${(session.user.app_metadata?.org_id as string) ?? ''}`,
        );
      } else {
        setUser(null);
        setTenant(null);
        setHeldRoles([]);
        setAccessToken(null);
        setOrgs([]);
        // Invalidate the gate with the session. Leaving a stale `true` here would
        // let the next account to sign in on this tab inherit the previous user's
        // portal access before its own probe resolves.
        setPortalAccess(null);
        portalAccessKeyRef.current = null;
        // KAN-118: half-finished club setup belongs to the person who started it. Once the
        // session ends, nobody else on this browser may resume it.
        clearAllGuidedSetupProgress();
      }
    });

    return () => {
      subscription?.unsubscribe();
    };
  }, [buildTenant, fetchOrgs, fetchPortalAccess]);

  // ---------------------------------------------------------------------------
  // switchOrg
  // ---------------------------------------------------------------------------

  const switchOrg = useCallback(
    async (orgId: string) => {
      const token = accessToken;
      if (!token) throw new Error('switchOrg called without an active session');

      // Find the target org display name so the overlay can show it immediately
      const targetOrg = orgs.find((o) => o.orgId === orgId);
      const targetDisplayName = targetOrg?.displayName ?? 'Organization';
      setSwitchingOrgName(targetDisplayName);
      switchingRef.current = true;
      try {
        // 1. Tell the API to update app_metadata
        const switchRes = await globalThis.fetch(`${API_URL}/api/auth/switch-org`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ orgId }),
        });

        if (!switchRes.ok) {
          // The API's error envelope is { error: <class name>, code, message }. `message` is the
          // sentence written for the person (who can fix it); `error` is only the class name.
          // `code` travels too, so the TopBar can word a known refusal in the person's language.
          const body = (await switchRes.json().catch(() => ({}))) as {
            error?: string;
            code?: string;
            message?: string;
          };
          throw new SwitchOrgError(
            body.message || body.error || `Switch failed: ${switchRes.status}`,
            typeof body.code === 'string' ? body.code : null,
          );
        }

        // 2. Refresh the JWT so the new org_id is baked in
        const { error: refreshError } = await supabase.auth.refreshSession();
        if (refreshError) throw refreshError;

        // 3. onAuthStateChange listener fires → rebuilds tenant with new org_id.
        //    Re-fetch orgs explicitly so isActive flags update.
        const {
          data: { session: newSession },
        } = await supabase.auth.getSession();
        if (newSession) {
          await fetchOrgs(newSession.access_token);
        }
      } finally {
        switchingRef.current = false;
        setSwitchingOrgName(null);
      }
    },
    [accessToken, fetchOrgs, orgs],
  );

  // ---------------------------------------------------------------------------
  // Derive activeOrg from orgs list (matches current JWT org_id)
  // ---------------------------------------------------------------------------

  const activeOrg: OrgMembership | null = orgs.find((o) => o.isActive) ?? null;

  // ---------------------------------------------------------------------------
  // Auth helpers
  // ---------------------------------------------------------------------------

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
  };

  const refreshOrgs = useCallback(async () => {
    if (accessToken) await fetchOrgs(accessToken);
  }, [accessToken, fetchOrgs]);

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        tenant,
        heldRoles,
        accessToken,
        loading,
        passwordRecovery,
        authError,
        clearAuthError,
        signIn,
        signOut,
        supabase,
        orgs,
        activeOrg,
        switchOrg,
        refreshOrgs,
        orgsLoading,
        switchingOrgName,
        portalAccess,
        portalAccessLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
